import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Mail, Phone, MapPin, Send, MessageCircle } from "lucide-react";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({ signal_number: "+31633136379", whatsapp_number: "+31633136379" });
  useEffect(() => { api.get("/site-settings").then((r) => setSettings((s) => ({ ...s, ...r.data }))).catch(() => {}); }, []);
  const signalHref = `https://signal.me/#p/${(settings.signal_number || "+31633136379").replace(/[^+0-9]/g,"")}`;
  const waDigits = (settings.whatsapp_number || "+31633136379").replace(/[^0-9]/g,"").replace(/^0/, "31");
  const waHref = `https://wa.me/${waDigits}?text=${encodeURIComponent("Hoi Isa, ik heb een vraag over een Reiki-sessie.")}`;
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send message.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page" className="container-refined py-16 md:py-24">
      <div className="max-w-2xl">
        <p className="eyebrow">{t("contact.eyebrow")}</p>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05] text-forest-900">{t("contact.title")}</h1>
      </div>

      <div className="mt-14 grid lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="card-warm">
            <p className="eyebrow"><MapPin className="inline h-3.5 w-3.5 mr-1" strokeWidth={1.5}/> {t("contact.postal_title")}</p>
            <h3 className="mt-3 font-serif text-2xl text-forest-900">Walstraat 58</h3>
            <p className="text-forest-700">7411 GN Deventer</p>
            <p className="mt-3 text-sm text-forest-700/90 leading-relaxed">{t("contact.postal_body")}</p>
          </div>
          <div className="card-warm border-l-4 border-l-wheat-500">
            <p className="eyebrow">{t("contact.visit_title")}</p>
            <p className="mt-3 text-sm text-forest-700 leading-relaxed">{t("contact.visit_body")}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <a href={signalHref} target="_blank" rel="noreferrer" className="card-warm !p-6 flex items-center gap-4" data-testid="contact-signal">
              <MessageCircle className="h-5 w-5 text-forest-800" strokeWidth={1.25}/>
              <div>
                <p className="eyebrow">Signal <span className="ml-1 text-[9px] text-wheat-700">(preferred)</span></p>
                <p className="mt-1 text-forest-900 font-medium">{settings.signal_number || "+31 6 33 13 63 79"}</p>
              </div>
            </a>
            <a href={waHref} target="_blank" rel="noreferrer" className="card-warm !p-6 flex items-center gap-4" data-testid="contact-whatsapp">
              <MessageCircle className="h-5 w-5 text-forest-800" strokeWidth={1.25}/>
              <div>
                <p className="eyebrow">WhatsApp</p>
                <p className="mt-1 text-forest-900 font-medium">0633136379</p>
                <p className="mt-1 text-[10px] text-forest-700/70">Signal is available too</p>
              </div>
            </a>
            <a href="tel:0633136379" className="card-warm !p-6 flex items-center gap-4" data-testid="contact-phone">
              <Phone className="h-5 w-5 text-forest-800" strokeWidth={1.25}/>
              <div>
                <p className="eyebrow">{t("contact.phone")}</p>
                <p className="mt-1 text-forest-900 font-medium">0633136379</p>
              </div>
            </a>
            <a href="mailto:info@sunbraider.nl" className="card-warm !p-6 flex items-center gap-4" data-testid="contact-email">
              <Mail className="h-5 w-5 text-forest-800" strokeWidth={1.25}/>
              <div>
                <p className="eyebrow">{t("contact.email")}</p>
                <p className="mt-1 text-forest-900 font-medium">info@sunbraider.nl</p>
              </div>
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="card-warm space-y-5" data-testid="contact-form">
          <h3 className="font-serif text-2xl text-forest-900">{t("contact.form_title")}</h3>
          <div>
            <label className="field-label">{t("contact.name")}</label>
            <input required data-testid="contact-name" className="field-input" value={form.name} onChange={onChange("name")} />
          </div>
          <div>
            <label className="field-label">{t("book.email")}</label>
            <input required type="email" data-testid="contact-email-input" className="field-input" value={form.email} onChange={onChange("email")} />
          </div>
          <div>
            <label className="field-label">{t("contact.subject")}</label>
            <input data-testid="contact-subject" className="field-input" value={form.subject} onChange={onChange("subject")} />
          </div>
          <div>
            <label className="field-label">{t("contact.message")}</label>
            <textarea rows={6} required data-testid="contact-message" className="field-input" value={form.message} onChange={onChange("message")} />
          </div>
          <button disabled={submitting} className="btn-primary" data-testid="contact-submit">
            <Send className="h-4 w-4" strokeWidth={1.5}/> {submitting ? t("common.loading") : t("contact.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
